import { Trans, useLingui } from '@lingui/react/macro';

import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import IconAdd from '@/icons/Add.svg';
import IconTrash from '@/icons/Trash.svg';

import { BrandsMultiSelect } from '../shared/BrandsMultiSelect';
import { LocaleInputs } from '../shared/LocaleInputs';

import { AnswerEditor } from './AnswerEditor';
import {
  AnswerInput,
  DefectTypeOption,
  QuestionInput,
  QuestionType,
  SubQuestionAnswerInput,
  SubQuestionInput,
  useQuestionTypeOptions,
} from './ConditionFormEditor';
import { getDefaultConfig, QuestionConfigEditor } from './QuestionConfigEditor';

const typeNeedsAnswers = (type: QuestionType) => type !== 'text-and-select';

export const QuestionEditor = ({
  question,
  questionIndex,
  onUpdate,
  onDelete,
  onAddAnswer,
  onUpdateAnswer,
  onDeleteAnswer,
  onAddSubQuestion,
  onDeleteSubQuestion,
  onUpdateSubQuestion,
  onAddSubQuestionAnswer,
  onUpdateSubQuestionAnswer,
  onDeleteSubQuestionAnswer,
  defectTypeOptions,
  showErrors,
}: {
  question: QuestionInput;
  questionIndex: number;
  onUpdate: (updates: Partial<QuestionInput>) => void;
  onDelete: () => void;
  onAddAnswer: () => void;
  onUpdateAnswer: (answerIndex: number, updates: Partial<AnswerInput>) => void;
  onDeleteAnswer: (answerIndex: number) => void;
  onAddSubQuestion: (answerIndex: number) => void;
  onDeleteSubQuestion: (answerIndex: number) => void;
  onUpdateSubQuestion: (answerIndex: number, updates: Partial<SubQuestionInput>) => void;
  onAddSubQuestionAnswer: (answerIndex: number) => void;
  onUpdateSubQuestionAnswer: (
    answerIndex: number,
    subAnswerIndex: number,
    updates: Partial<SubQuestionAnswerInput>
  ) => void;
  onDeleteSubQuestionAnswer: (answerIndex: number, subAnswerIndex: number) => void;
  defectTypeOptions: DefectTypeOption[];
  showErrors: boolean;
}) => {
  const { t, i18n } = useLingui();
  const questionTypeOptions = useQuestionTypeOptions();

  const handleTypeChange = (newType: QuestionType) => {
    const updates: Partial<QuestionInput> = { type: newType };

    if (newType !== question.type) {
      updates.config = getDefaultConfig(newType);

      if (!typeNeedsAnswers(newType)) {
        updates.answers = [];
      }
    }
    onUpdate(updates);
  };

  return (
    <Box>
      <Stack gap="1rem">
        <Stack row alignItems="center" justifyContent="space-between">
          <Title size="300" weight="bold">
            {i18n._({
              id: 'settings.condition-forms.question',
              message: 'Question {order}',
              values: { order: questionIndex + 1 },
            })}
          </Title>
          <Button
            iconOnly
            variant="secondary-danger"
            onPress={onDelete}
            ariaLabel={t({
              id: 'settings.condition-forms.delete-question',
              message: 'Delete question',
            })}
          >
            <IconTrash />
          </Button>
        </Stack>

        <LocaleInputs
          label={t({
            id: 'settings.condition-forms.question-title',
            message: 'Question title',
          })}
          placeholder={t({
            id: 'settings.condition-forms.question-title',
            message: 'Question title',
          })}
          value={question.title}
          setValue={(title) => onUpdate({ title })}
          showErrors={showErrors}
        />

        <InputSelect
          label={t({
            id: 'settings.condition-forms.question-type',
            message: 'Question type',
          })}
          options={questionTypeOptions}
          value={questionTypeOptions.find((opt) => opt.value === question.type)}
          getOptionValue={(opt) => opt.value}
          getOptionLabel={(opt) => opt.label}
          onChange={(option) => option && handleTypeChange(option.value)}
        />

        <BrandsMultiSelect
          selectedBrandIds={question.brands ?? []}
          setBrandIds={(brandIds) => onUpdate({ brands: brandIds })}
        />

        <QuestionConfigEditor
          config={question.config}
          type={question.type}
          onConfigChange={(config) => onUpdate({ config })}
          showErrors={showErrors}
        />

        {typeNeedsAnswers(question.type) && (
          <Stack gap="0.5rem">
            <Title size="300" weight="bold">
              <Trans id="settings.condition-forms.answers">Answers</Trans>
            </Title>

            {question.answers.map((answer, answerIndex) => (
              <AnswerEditor
                key={answer.id ?? answerIndex}
                answer={answer}
                answerIndex={answerIndex}
                onUpdate={(updates) => onUpdateAnswer(answerIndex, updates)}
                onDelete={() => onDeleteAnswer(answerIndex)}
                onAddSubQuestion={() => onAddSubQuestion(answerIndex)}
                onDeleteSubQuestion={() => onDeleteSubQuestion(answerIndex)}
                onUpdateSubQuestion={(updates) => onUpdateSubQuestion(answerIndex, updates)}
                onAddSubQuestionAnswer={() => onAddSubQuestionAnswer(answerIndex)}
                onUpdateSubQuestionAnswer={(subAnswerIndex, updates) =>
                  onUpdateSubQuestionAnswer(answerIndex, subAnswerIndex, updates)
                }
                onDeleteSubQuestionAnswer={(subAnswerIndex) =>
                  onDeleteSubQuestionAnswer(answerIndex, subAnswerIndex)
                }
                defectTypeOptions={defectTypeOptions}
                showErrors={showErrors}
                questionBrandIds={question.brands}
              />
            ))}

            {question.answers.length === 0 && (
              <Paragraph size="200" color={showErrors ? 'danger' : 'secondary'}>
                <Trans id="settings.condition-forms.no-answers">
                  No answers yet. Add at least one answer.
                </Trans>
              </Paragraph>
            )}

            <Button variant="secondary" onPress={onAddAnswer}>
              <IconAdd />
              <Trans id="settings.condition-forms.add-answer">Add answer</Trans>
            </Button>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};
